import React, { useState, useEffect, useRef } from 'react';
import { getAssessment, updateAssessment } from '../firebase/firestore';
import {
  QUESTIONS, LIKERT_QUESTIONS, APTITUDE_QUESTIONS, SECTIONS,
  LIKERT_LABELS, QUESTIONS_PER_PAGE,
  calculateScores, generateReport,
} from '../data/assessmentData';

// ─── Colour Palettes ───────────────────────────────────────────────────────────
const hollandColors = { R:'#f59e0b', I:'#3b82f6', A:'#ec4899', S:'#10b981', E:'#ef4444', C:'#8b5cf6' };
const hollandNames  = { R:'Realistic', I:'Investigative', A:'Artistic', S:'Social', E:'Enterprising', C:'Conventional' };
const bigFiveColors = ['#7c3aed','#3b82f6','#10b981','#f59e0b','#06b6d4'];
const miColors      = ['#7c3aed','#3b82f6','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899','#34d399','#a78bfa','#fb923c'];
const aptColors     = { numerical:'#3b82f6', verbal:'#10b981', logical:'#7c3aed', abstract:'#f59e0b' };
const aptLabels     = { numerical:'Numerical', verbal:'Verbal', logical:'Logical', abstract:'Abstract' };

// ─── Reusable Components ───────────────────────────────────────────────────────
const ScoreBar = ({ label, value, color, band, delay = 0 }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
      <span style={{ fontSize:13, color:'#374151', fontWeight:500 }}>{label}</span>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        {band && (
          <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10,
            background: band.color + '18', color: band.color }}>
            {band.label}
          </span>
        )}
        <span style={{ fontSize:14, color:color, fontWeight:800 }}>{Math.round(value)}%</span>
      </div>
    </div>
    <div style={{ height:8, background:'rgba(0,0,0,0.06)', borderRadius:4, overflow:'hidden' }}>
      <div style={{
        height:'100%', width: Math.round(value) + '%', background: color,
        borderRadius:4, transition: 'width 1s ease ' + delay + 'ms',
      }} />
    </div>
  </div>
);

const SectionDivider = ({ icon, title, subtitle }) => (
  <div style={{ display:'flex', alignItems:'center', gap:14, margin:'32px 0 24px',
    padding:'16px 20px', background:'rgba(15,23,42,0.04)', borderRadius:14,
    border:'1px solid rgba(15,23,42,0.07)' }}>
    <span style={{ fontSize:26 }}>{icon}</span>
    <div>
      <div style={{ fontSize:15, fontWeight:800, color:'#0f172a' }}>{title}</div>
      <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{subtitle}</div>
    </div>
  </div>
);

// ─── PREMIUM REPORT VIEW ──────────────────────────────────────────────────────
function ReportView({ report, assessment }) {
  const {
    hollandTypeLabel, personalityArchetype, archetypeDesc,
    hollandRanked, bigFiveRanked, intelligencesRanked, aptitudeRanked,
    topCareers, personalityInsights, strengths,
    learningStyle, cognitiveProfile, aptitudeAnalysis,
    aptBand, aptSubBands, aptitudeTotal,
    studyAbroadReadiness, actionPlan, overallScore,
  } = report;

  const completedDate = assessment.completedAt
    ? new Date(assessment.completedAt).toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })
    : new Date().toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' });

  // Hex radial / spider chart for Holland (CSS-only bars in circular layout)
  const HollandRadar = () => (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginTop:16 }}>
      {hollandRanked.map(({ code, score }) => (
        <div key={code} style={{
          padding:'12px 14px', borderRadius:12, background: hollandColors[code] + '10',
          border:'1px solid ' + hollandColors[code] + '28', textAlign:'center',
        }}>
          <div style={{ fontSize:22, fontWeight:800, color:hollandColors[code] }}>{Math.round(score)}%</div>
          <div style={{ fontSize:11, fontWeight:700, color:'#374151', marginTop:2 }}>{hollandNames[code]}</div>
          <div style={{ height:4, background:'rgba(0,0,0,0.06)', borderRadius:2, marginTop:8, overflow:'hidden' }}>
            <div style={{ height:'100%', width:Math.round(score)+'%', background:hollandColors[code], borderRadius:2 }} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ maxWidth:820, margin:'0 auto', padding:'0 0 80px', fontFamily:"'Inter',sans-serif" }}>

      {/* ── Hero Cover ────────────────────────────────────────────────────── */}
      <div style={{
        position:'relative', overflow:'hidden', borderRadius:'0 0 28px 28px',
        background:'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
        padding:'48px 32px 40px', marginBottom:28, color:'#fff',
      }}>
        {/* Decorative orbs */}
        <div style={{ position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%',
          background:'rgba(124,58,237,0.15)', filter:'blur(40px)', pointerEvents:'none' }} />
        <div style={{ position:'absolute', bottom:-20, left:-20, width:160, height:160, borderRadius:'50%',
          background:'rgba(59,130,246,0.15)', filter:'blur(30px)', pointerEvents:'none' }} />

        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ fontSize:11, letterSpacing:3, fontWeight:700, color:'rgba(255,255,255,0.5)',
            textTransform:'uppercase', marginBottom:16 }}>
            Clarion Career Intelligence Report
          </div>
          <h1 style={{ fontSize:30, fontWeight:900, margin:'0 0 8px', lineHeight:1.15 }}>
            {assessment.leadName}
          </h1>
          <div style={{ fontSize:14, color:'rgba(255,255,255,0.55)', marginBottom:24 }}>
            Assessment completed {completedDate}
          </div>

          {/* 3-pill stats row */}
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            <div style={{ padding:'10px 18px', borderRadius:12,
              background:'rgba(255,255,255,0.08)', backdropFilter:'blur(8px)',
              border:'1px solid rgba(255,255,255,0.14)' }}>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:1, textTransform:'uppercase' }}>Career Type</div>
              <div style={{ fontSize:14, fontWeight:800, marginTop:2 }}>{hollandTypeLabel}</div>
            </div>
            <div style={{ padding:'10px 18px', borderRadius:12,
              background:'rgba(255,255,255,0.08)', backdropFilter:'blur(8px)',
              border:'1px solid rgba(255,255,255,0.14)' }}>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', letterSpacing:1, textTransform:'uppercase' }}>Personality</div>
              <div style={{ fontSize:14, fontWeight:800, marginTop:2 }}>{personalityArchetype}</div>
            </div>
            <div style={{ padding:'10px 18px', borderRadius:12,
              background:'rgba(124,58,237,0.35)', backdropFilter:'blur(8px)',
              border:'1px solid rgba(124,58,237,0.5)' }}>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.6)', letterSpacing:1, textTransform:'uppercase' }}>Overall Score</div>
              <div style={{ fontSize:22, fontWeight:900, marginTop:2 }}>{overallScore}<span style={{ fontSize:12, opacity:0.7 }}>/99</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Executive Summary ─────────────────────────────────────────────── */}
      <div style={{ background:'#fff', borderRadius:20, padding:28, marginBottom:20,
        boxShadow:'0 4px 24px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.05)' }}>
        <SectionDivider icon="🔍" title="Executive Summary" subtitle="Key findings from your assessment" />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div style={{ padding:18, borderRadius:14, background:'rgba(124,58,237,0.04)',
            border:'1px solid rgba(124,58,237,0.12)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#7c3aed', textTransform:'uppercase',
              letterSpacing:1, marginBottom:8 }}>Personality Archetype</div>
            <div style={{ fontSize:16, fontWeight:800, color:'#0f172a', marginBottom:8 }}>{personalityArchetype}</div>
            <div style={{ fontSize:13, color:'#475569', lineHeight:1.6 }}>{archetypeDesc}</div>
          </div>
          <div style={{ padding:18, borderRadius:14, background:'rgba(16,185,129,0.04)',
            border:'1px solid rgba(16,185,129,0.12)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#059669', textTransform:'uppercase',
              letterSpacing:1, marginBottom:8 }}>Cognitive Profile</div>
            <div style={{ fontSize:13, color:'#374151', lineHeight:1.65 }}>{cognitiveProfile}</div>
          </div>
        </div>
        {studyAbroadReadiness && (
          <div style={{ marginTop:16, padding:18, borderRadius:14,
            background:'linear-gradient(135deg, rgba(59,130,246,0.05), rgba(16,185,129,0.05))',
            border:'1px solid rgba(59,130,246,0.15)', display:'flex', alignItems:'flex-start', gap:14 }}>
            <div style={{ fontSize:26, flexShrink:0 }}>✈️</div>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                <span style={{ fontSize:14, fontWeight:700, color:'#0f172a' }}>Study Abroad Readiness</span>
                <span style={{ padding:'3px 10px', borderRadius:10, fontSize:11, fontWeight:700,
                  background: studyAbroadReadiness.band.color + '18',
                  color: studyAbroadReadiness.band.color }}>
                  {studyAbroadReadiness.score}% · {studyAbroadReadiness.band.label}
                </span>
              </div>
              <div style={{ fontSize:13, color:'#475569', lineHeight:1.6, marginBottom:8 }}>
                {studyAbroadReadiness.note}
              </div>

            </div>
          </div>
        )}
      </div>

      {/* ── Career Interest Profile (Holland) ──────────────────────────────── */}
      <div style={{ background:'#fff', borderRadius:20, padding:28, marginBottom:20,
        boxShadow:'0 4px 24px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.05)' }}>
        <SectionDivider icon="🎯" title="Career Interest Profile"
          subtitle="Holland's RIASEC — the world's most validated career interest model (Holland, 1994)" />
        <HollandRadar />
        <div style={{ marginTop:20 }}>
          {hollandRanked.map(({ code, score }, i) => (
            <ScoreBar key={code} label={hollandNames[code]} value={Math.round(score)}
              color={hollandColors[code]}
              band={i < 2 ? { label: i === 0 ? 'Primary' : 'Secondary', color: hollandColors[code] } : null}
              delay={i * 80} />
          ))}
        </div>
        <div style={{ marginTop:18, padding:'14px 18px', borderRadius:12,
          background:'rgba(124,58,237,0.05)', border:'1px solid rgba(124,58,237,0.12)',
          fontSize:13, color:'#374151', lineHeight:1.65 }}>
          <strong style={{ color:'#7c3aed' }}>Your dominant type: {hollandTypeLabel}</strong>
          <br />Your top two interest codes define careers that will feel natural, energising, and aligned with who you are. Roles that match your primary and secondary codes consistently produce higher job satisfaction and long-term success.
        </div>
      </div>

      {/* ── Top Career Matches ───────────────────────────────────────────── */}
      <div style={{ background:'#fff', borderRadius:20, padding:28, marginBottom:20,
        boxShadow:'0 4px 24px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.05)' }}>
        <SectionDivider icon="🏆" title="Your Top Career Matches"
          subtitle="Ranked by compatibility across interests, intelligences, and aptitude" />
        {topCareers.map((career, i) => (
          <div key={career.id} style={{
            marginBottom:16, borderRadius:16, overflow:'hidden',
            border: i === 0 ? '1.5px solid rgba(124,58,237,0.3)' : '1px solid rgba(0,0,0,0.07)',
            boxShadow: i === 0 ? '0 6px 24px rgba(124,58,237,0.1)' : '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            {/* Match header */}
            <div style={{
              padding:'14px 18px',
              background: i === 0
                ? 'linear-gradient(135deg, rgba(124,58,237,0.08), rgba(59,130,246,0.06))'
                : 'rgba(0,0,0,0.02)',
              borderBottom:'1px solid rgba(0,0,0,0.05)',
              display:'flex', alignItems:'center', gap:12,
            }}>
              <span style={{ fontSize:26, flexShrink:0 }}>{career.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  <span style={{ fontSize:15, fontWeight:800, color:'#0f172a' }}>#{i+1} {career.title}</span>
                  {i === 0 && (
                    <span style={{ padding:'2px 10px', borderRadius:10, background:'#7c3aed',
                      color:'#fff', fontSize:10, fontWeight:800, letterSpacing:0.5 }}>
                      BEST MATCH
                    </span>
                  )}
                </div>
                <div style={{ fontSize:12, color:'#64748b', marginTop:3 }}>{career.workStyle}</div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontSize:26, fontWeight:900,
                  color: i === 0 ? '#7c3aed' : i === 1 ? '#10b981' : '#3b82f6' }}>
                  {career.matchScore}%
                </div>
                <div style={{ fontSize:10, color:'#94a3b8', marginTop:1 }}>compatibility</div>
              </div>
            </div>

            {/* Match bar */}
            <div style={{ height:4, background:'rgba(0,0,0,0.04)' }}>
              <div style={{
                height:'100%', width: career.matchScore + '%',
                background: i === 0 ? 'linear-gradient(90deg,#7c3aed,#3b82f6)'
                  : i === 1 ? '#10b981' : '#3b82f6',
                transition:'width 1s ease ' + (i*100) + 'ms',
              }} />
            </div>

            {/* Details grid */}
            <div style={{ padding:'16px 18px' }}>
              <div style={{ fontSize:13, color:'#475569', lineHeight:1.65, marginBottom:14 }}>
                {career.description}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:14, fontSize:12 }}>
                <div>
                  <div style={{ fontWeight:700, color:'#0f172a', marginBottom:6, fontSize:11,
                    textTransform:'uppercase', letterSpacing:0.5 }}>🎓 Recommended Degrees</div>
                  {career.degrees.slice(0,3).map((d,j) => (
                    <div key={j} style={{ color:'#475569', padding:'3px 0', display:'flex', gap:6 }}>
                      <span style={{ color:'#7c3aed', flexShrink:0 }}>→</span>{d}
                    </div>
                  ))}
                </div>

              </div>
              <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid rgba(0,0,0,0.05)',
                display:'flex', gap:20, flexWrap:'wrap', fontSize:12 }}>
                <div>
                  <div style={{ fontWeight:700, color:'#0f172a', marginBottom:4, fontSize:11,
                    textTransform:'uppercase', letterSpacing:0.5 }}>📋 Entry Exams</div>
                  <div style={{ color:'#64748b' }}>{career.exams.join(' · ')}</div>
                </div>
                <div>
                  <div style={{ fontWeight:700, color:'#0f172a', marginBottom:4, fontSize:11,
                    textTransform:'uppercase', letterSpacing:0.5 }}>💰 Salary Range</div>
                  <div style={{ color:'#059669', fontWeight:500 }}>{career.salaryRange}</div>
                </div>
              </div>
              <div style={{ marginTop:10, padding:'8px 12px', background:'rgba(59,130,246,0.04)',
                borderRadius:8, fontSize:12, color:'#1d4ed8' }}>
                📈 {career.growth}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Personality Profile ──────────────────────────────────────────── */}
      <div style={{ background:'#fff', borderRadius:20, padding:28, marginBottom:20,
        boxShadow:'0 4px 24px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.05)' }}>
        <SectionDivider icon="🧬" title="Personality Profile — Big Five"
          subtitle="The most scientifically validated personality model in psychology (BFI-10, Rammstedt & John, 2007)" />
        {bigFiveRanked.map(({ trait, score }, i) => (
          <ScoreBar key={trait} label={trait} value={Math.round(score)}
            color={bigFiveColors[i]} delay={i*80} />
        ))}
        {personalityInsights.length > 0 && (
          <div style={{ marginTop:20 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#0f172a', textTransform:'uppercase',
              letterSpacing:0.5, marginBottom:12 }}>What this means for you:</div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {personalityInsights.map((insight, i) => (
                <div key={i} style={{ display:'flex', gap:12, padding:'12px 16px',
                  background:'rgba(124,58,237,0.03)', borderRadius:10,
                  border:'1px solid rgba(124,58,237,0.08)', fontSize:13, color:'#374151', lineHeight:1.6 }}>
                  <span style={{ color:'#7c3aed', fontWeight:800, flexShrink:0, fontSize:16 }}>✦</span>
                  {insight}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Cognitive Aptitude ──────────────────────────────────────────── */}
      <div style={{ background:'#fff', borderRadius:20, padding:28, marginBottom:20,
        boxShadow:'0 4px 24px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.05)' }}>
        <SectionDivider icon="🧮" title="Cognitive Aptitude Battery"
          subtitle="Numerical · Verbal · Logical · Abstract Reasoning" />

        {/* Overall aptitude gauge */}
        <div style={{ display:'flex', alignItems:'center', gap:20, padding:'18px 22px',
          background:'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(124,58,237,0.06))',
          borderRadius:14, border:'1px solid rgba(245,158,11,0.18)', marginBottom:20 }}>
          <div style={{ textAlign:'center', flexShrink:0 }}>
            <div style={{ fontSize:40, fontWeight:900, color:'#f59e0b', lineHeight:1 }}>{aptitudeTotal}%</div>
            <div style={{ fontSize:10, color:'#92400e', fontWeight:700, textTransform:'uppercase',
              letterSpacing:1, marginTop:4 }}>Overall</div>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <span style={{ fontSize:14, fontWeight:700, color:'#0f172a' }}>Aptitude Score</span>
              <span style={{ padding:'3px 10px', borderRadius:10, fontSize:11, fontWeight:700,
                background: aptBand.color + '18', color: aptBand.color }}>
                {aptBand.label}
              </span>
            </div>
            <div style={{ fontSize:13, color:'#475569', lineHeight:1.6 }}>{aptitudeAnalysis}</div>
          </div>
        </div>

        {/* Category bars */}
        {aptitudeRanked && aptitudeRanked.map(({ category, score }) => (
          <ScoreBar key={category}
            label={aptLabels[category] + ' Reasoning'}
            value={score}
            color={aptColors[category]}
            band={aptSubBands[category]} />
        ))}
      </div>

      {/* ── Multiple Intelligences ──────────────────────────────────────── */}
      <div style={{ background:'#fff', borderRadius:20, padding:28, marginBottom:20,
        boxShadow:'0 4px 24px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.05)' }}>
        <SectionDivider icon="⚡" title="Intelligence Strengths Profile"
          subtitle="Based on Gardner's Multiple Intelligence Theory (1983) with contemporary extensions" />
        {intelligencesRanked.map(({ intelligence, score }, i) => (
          <ScoreBar key={intelligence} label={intelligence} value={Math.round(score)}
            color={miColors[i % miColors.length]} delay={i*60} />
        ))}
      </div>

      {/* ── Key Strengths ───────────────────────────────────────────────── */}
      {strengths.length > 0 && (
        <div style={{ background:'#fff', borderRadius:20, padding:28, marginBottom:20,
          boxShadow:'0 4px 24px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.05)' }}>
          <SectionDivider icon="💎" title="Your Signature Strengths"
            subtitle="Your top intelligence areas translated into professional capabilities" />
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {strengths.map((s, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px',
                background: i === 0 ? 'rgba(124,58,237,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius:12,
                border: i === 0 ? '1px solid rgba(124,58,237,0.18)' : '1px solid rgba(0,0,0,0.06)' }}>
                <div style={{ width:28, height:28, borderRadius:8, flexShrink:0,
                  background: miColors[i] + '18',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:13, fontWeight:800, color:miColors[i] }}>
                  {i + 1}
                </div>
                <div style={{ fontSize:13, color:'#374151', fontWeight: i===0 ? 600 : 400, lineHeight:1.5 }}>{s}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Learning Style ──────────────────────────────────────────────── */}
      <div style={{ background:'#fff', borderRadius:20, padding:28, marginBottom:20,
        boxShadow:'0 4px 24px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.05)' }}>
        <SectionDivider icon="📖" title="Your Optimal Learning Style"
          subtitle="Personalised study strategy based on your intelligence profile" />
        <div style={{ fontSize:14, color:'#374151', lineHeight:1.8, padding:'16px 20px',
          background:'rgba(16,185,129,0.05)', borderRadius:12,
          border:'1px solid rgba(16,185,129,0.14)' }}>
          {learningStyle}
        </div>
      </div>

      {/* ── Action Plan ─────────────────────────────────────────────────── */}
      {actionPlan && (
        <div style={{ background:'#fff', borderRadius:20, padding:28, marginBottom:20,
          boxShadow:'0 4px 24px rgba(0,0,0,0.06)', border:'1px solid rgba(0,0,0,0.05)' }}>
          <SectionDivider icon="🗺️" title="Your 6-Month Action Plan"
            subtitle="Personalised roadmap to your ideal career pathway" />
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {actionPlan.map((phase, pi) => (
              <div key={pi} style={{ borderRadius:14, overflow:'hidden',
                border:'1px solid ' + phase.color + '28' }}>
                <div style={{ padding:'12px 18px',
                  background:'linear-gradient(135deg,' + phase.color + '12,' + phase.color + '06)' }}>
                  <div style={{ fontSize:13, fontWeight:800, color: phase.color }}>{phase.phase}</div>
                </div>
                <div style={{ padding:'14px 18px' }}>
                  {phase.steps.map((step, si) => (
                    <div key={si} style={{ display:'flex', gap:12, marginBottom:10,
                      fontSize:13, color:'#374151', lineHeight:1.55 }}>
                      <div style={{ width:22, height:22, borderRadius:6, flexShrink:0,
                        background: phase.color + '15',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:11, fontWeight:800, color:phase.color }}>
                        {si + 1}
                      </div>
                      {step}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <div style={{ textAlign:'center', padding:'24px 20px 8px', fontSize:11, color:'#94a3b8',
        lineHeight:1.8 }}>
        <div style={{ marginBottom:6, fontSize:13, fontWeight:600, color:'#cbd5e1' }}>
          Clarion Career Intelligence Report
        </div>
        Based on Holland RIASEC (1994) · Big Five BFI-10 (Rammstedt & John, 2007) · Multiple Intelligences (Gardner, 1983) · Cognitive Aptitude Battery
        <br />Report generated by Clarion CRM · Confidential — prepared exclusively for {assessment.leadName}
      </div>
    </div>
  );
}

// ─── MAIN TEST PAGE ─────────────────────────────────────────────────────────
export default function PsychometricTest({ assessmentId }) {
  const [assessment, setAssessment] = useState(null);
  const [notFound,   setNotFound]   = useState(false);
  const [permError,  setPermError]  = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [responses,  setResponses]  = useState({});
  const [page,       setPage]       = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [report,     setReport]     = useState(null);
  const [animDir,    setAnimDir]    = useState('next'); // 'next' | 'prev'
  const topRef = useRef(null);

  // Flatten all questions in section order
  const ALL_QUESTIONS = [...LIKERT_QUESTIONS, ...APTITUDE_QUESTIONS];
  const TOTAL_PAGES = Math.ceil(ALL_QUESTIONS.length / QUESTIONS_PER_PAGE);
  const pageQuestions = ALL_QUESTIONS.slice(page * QUESTIONS_PER_PAGE, (page + 1) * QUESTIONS_PER_PAGE);
  const pageAnswered  = pageQuestions.every(q => responses[q.id] != null);
  const totalAnswered = ALL_QUESTIONS.filter(q => responses[q.id] != null).length;
  const progress      = Math.round((totalAnswered / ALL_QUESTIONS.length) * 100);

  // Current section for header colour
  const currentSection = (() => {
    const qStart = page * QUESTIONS_PER_PAGE;
    return SECTIONS.find(s => qStart >= s.range[0] && qStart <= s.range[1]) || SECTIONS[0];
  })();

  useEffect(() => {
    if (!assessmentId) { setNotFound(true); setLoading(false); return; }
    getAssessment(assessmentId).then(snap => {
      if (!snap.exists()) { setNotFound(true); setLoading(false); return; }
      const data = { id: snap.id, ...snap.data() };
      setAssessment(data);
      if (data.status === 'completed' && data.report) setReport(data.report);
      else if (data.responses) setResponses(data.responses);
      setLoading(false);
    }).catch(e => {
      if (e?.code === 'permission-denied') setPermError(true);
      else setNotFound(true);
      setLoading(false);
    });
  }, [assessmentId]);

  const handleAnswer = (qId, val) => setResponses(p => ({ ...p, [qId]: val }));

  const navigate = (dir) => {
    setAnimDir(dir);
    if (dir === 'next' && page < TOTAL_PAGES - 1) setPage(p => p + 1);
    if (dir === 'prev' && page > 0) setPage(p => p - 1);
    setTimeout(() => topRef.current?.scrollIntoView({ behavior:'smooth' }), 50);
  };

  const handleSubmit = async () => {
    if (totalAnswered < ALL_QUESTIONS.length) return;
    setSubmitting(true);
    try {
      const scores = calculateScores(responses);
      const generatedReport = generateReport(scores);
      await updateAssessment(assessmentId, {
        responses, scores, report: generatedReport,
        status: 'completed', completedAt: new Date().toISOString(),
      });
      setReport(generatedReport);
      topRef.current?.scrollIntoView({ behavior:'smooth' });
    } catch (err) {
      console.error(err);
      alert('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      background:'linear-gradient(135deg,#0f172a,#1e1b4b)', fontFamily:"'Inter',sans-serif" }}>
      <div style={{ textAlign:'center', color:'#fff' }}>
        <div style={{ width:52, height:52, borderRadius:14,
          background:'linear-gradient(135deg,#7c3aed,#3b82f6)',
          margin:'0 auto 16px', animation:'spin 1s linear infinite' }} />
        <div style={{ fontSize:14, color:'rgba(255,255,255,0.6)' }}>Loading your assessment…</div>
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      </div>
    </div>
  );

  // ── Error states ─────────────────────────────────────────────────────────
  const ErrorScreen = ({ emoji, title, body, showRetry }) => (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      background:'linear-gradient(135deg,#f8fafc,#ede9fe30)', fontFamily:"'Inter',sans-serif", padding:24 }}>
      <div style={{ textAlign:'center', maxWidth:420 }}>
        <div style={{ fontSize:64, marginBottom:20 }}>{emoji}</div>
        <div style={{ fontSize:22, fontWeight:800, color:'#0f172a', marginBottom:10 }}>{title}</div>
        <div style={{ fontSize:14, color:'#64748b', lineHeight:1.7, marginBottom:24 }}>{body}</div>
        {showRetry && (
          <button onClick={() => window.location.reload()}
            style={{ padding:'12px 28px', borderRadius:12, background:'#7c3aed', color:'#fff',
              border:'none', cursor:'pointer', fontSize:14, fontWeight:700,
              boxShadow:'0 4px 16px rgba(124,58,237,0.35)' }}>
            Try Again
          </button>
        )}
      </div>
    </div>
  );

  if (permError) return <ErrorScreen emoji="🔐" title="Access not configured"
    body="The Firestore rules need to be published. Ask your administrator to deploy the rules from Firebase Console, then try again."
    showRetry={true} />;

  if (notFound) return <ErrorScreen emoji="🔍" title="Assessment not found"
    body="This link may be invalid or has expired. Please ask your counsellor to send a fresh assessment link."
    showRetry={false} />;

  // ── Completed — show report ───────────────────────────────────────────────
  if (report) return (
    <div ref={topRef}
      style={{ minHeight:'100vh', background:'linear-gradient(160deg,#f8fafc 0%,#ede9fe20 100%)',
        fontFamily:"'Inter',sans-serif" }}>
      <ReportView report={report} assessment={assessment} />
    </div>
  );

  // ── Determine question type for this page ─────────────────────────────────
  const isAptitudePage = currentSection.type === 'mcq';

  // ── Test UI ──────────────────────────────────────────────────────────────
  return (
    <div ref={topRef}
      style={{ minHeight:'100vh', background:'linear-gradient(160deg,#f8fafc 0%,#ede9fe20 100%)',
        fontFamily:"'Inter',sans-serif", paddingBottom:60 }}>

      {/* Top header bar */}
      <div style={{ position:'sticky', top:0, zIndex:10,
        background:'rgba(255,255,255,0.95)', backdropFilter:'blur(16px)',
        borderBottom:'1px solid rgba(0,0,0,0.06)', padding:'0 20px' }}>
        <div style={{ maxWidth:680, margin:'0 auto', padding:'14px 0',
          display:'flex', alignItems:'center', gap:14 }}>

          {/* Logo mark */}
          <div style={{ width:36, height:36, borderRadius:10, flexShrink:0,
            background:'linear-gradient(135deg,#7c3aed,#3b82f6)',
            display:'flex', alignItems:'center', justifyContent:'center',
            color:'#fff', fontWeight:900, fontSize:16 }}>C</div>

          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, fontWeight:800, color:'#0f172a', letterSpacing:0.2 }}>
              Clarion Career Assessment
            </div>
            <div style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>
              {assessment?.leadName} · {totalAnswered}/{ALL_QUESTIONS.length} answered
            </div>
          </div>

          {/* Section pills */}
          <div style={{ display:'flex', gap:4, flexShrink:0 }}>
            {SECTIONS.map((s, si) => {
              const sectionStart = s.range[0];
              const sectionEnd   = s.range[1];
              const answered     = ALL_QUESTIONS.filter(q => q.id >= sectionStart && q.id <= sectionEnd && responses[q.id] != null).length;
              const total        = sectionEnd - sectionStart + 1;
              const done         = answered === total;
              const active       = currentSection.id === s.id;
              return (
                <div key={s.id} title={s.title}
                  style={{ width:28, height:28, borderRadius:7, display:'flex', alignItems:'center',
                    justifyContent:'center', fontSize:13,
                    background: done ? s.color + '18' : active ? s.color + '10' : 'rgba(0,0,0,0.04)',
                    border: active ? '1.5px solid ' + s.color : '1px solid rgba(0,0,0,0.06)',
                    transition:'all 0.2s',
                  }}>
                  {done ? '✓' : s.icon}
                </div>
              );
            })}
          </div>
        </div>

        {/* Progress rail */}
        <div style={{ height:3, background:'rgba(0,0,0,0.04)' }}>
          <div style={{ height:'100%', width: progress + '%',
            background:'linear-gradient(90deg,' + currentSection.color + ',#3b82f6)',
            transition:'width 0.4s ease', borderRadius:2 }} />
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth:680, margin:'0 auto', padding:'28px 20px 0' }}>

        {/* Section banner */}
        <div style={{ marginBottom:24, padding:'18px 22px', borderRadius:16,
          background: currentSection.color + '0c',
          border:'1px solid ' + currentSection.color + '28',
          display:'flex', alignItems:'flex-start', gap:14 }}>
          <span style={{ fontSize:28, flexShrink:0 }}>{currentSection.icon}</span>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#0f172a' }}>{currentSection.title}</div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:3, lineHeight:1.5 }}>
              {currentSection.subtitle}
            </div>
            <div style={{ fontSize:11, color: currentSection.color, fontWeight:600, marginTop:8 }}>
              Section {SECTIONS.findIndex(s => s.id === currentSection.id) + 1} of {SECTIONS.length}
              {' · '}Page {page + 1} of {TOTAL_PAGES}
              {' · '}Questions {page * QUESTIONS_PER_PAGE + 1}–{Math.min((page + 1) * QUESTIONS_PER_PAGE, ALL_QUESTIONS.length)}
            </div>
          </div>
        </div>

        {/* Questions */}
        {pageQuestions.map((q, qi) => {
          const answered = responses[q.id] != null;
          const isAptQ   = q.options != null;

          return (
            <div key={q.id} style={{
              background:'#fff', borderRadius:16, padding:22, marginBottom:16,
              boxShadow:'0 2px 16px rgba(0,0,0,0.05)',
              border:'1.5px solid ' + (answered ? 'rgba(16,185,129,0.3)' : 'rgba(0,0,0,0.06)'),
              transition:'border-color 0.2s, box-shadow 0.2s',
            }}>
              {/* Question label */}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                <div style={{ width:28, height:28, borderRadius:7, flexShrink:0,
                  background: answered ? 'rgba(16,185,129,0.1)' : currentSection.color + '10',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:11, fontWeight:800, color: answered ? '#059669' : currentSection.color }}>
                  {answered ? '✓' : ('Q' + (page * QUESTIONS_PER_PAGE + qi + 1))}
                </div>
                {isAptQ && (
                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:6,
                    background:'rgba(245,158,11,0.1)', color:'#d97706', textTransform:'uppercase',
                    letterSpacing:0.5 }}>
                    {q.category}
                  </span>
                )}
              </div>

              {/* Question text */}
              <div style={{ fontSize:15, fontWeight:600, color:'#0f172a', marginBottom:18,
                lineHeight:1.5 }}>
                {q.text}
              </div>

              {/* Likert scale */}
              {!isAptQ && (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:6 }}>
                    {LIKERT_LABELS.map(opt => {
                      const sel = responses[q.id] === opt.value;
                      return (
                        <button key={opt.value} onClick={() => handleAnswer(q.id, opt.value)}
                          title={opt.label}
                          style={{
                            padding:'10px 4px', borderRadius:10,
                            border:'2px solid ' + (sel ? opt.color : 'rgba(0,0,0,0.08)'),
                            background: sel ? opt.color : 'transparent',
                            color: sel ? '#fff' : '#64748b',
                            cursor:'pointer', fontSize:13, fontWeight: sel ? 800 : 400,
                            transition:'all 0.15s',
                          }}>
                          {opt.value}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginTop:6,
                    fontSize:10, color:'#94a3b8' }}>
                    <span>Strongly Disagree</span>
                    <span>Strongly Agree</span>
                  </div>
                </>
              )}

              {/* MCQ options */}
              {isAptQ && (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {q.options.map((opt, oi) => {
                    const sel = responses[q.id] === oi;
                    const letters = ['A', 'B', 'C', 'D'];
                    return (
                      <button key={oi} onClick={() => handleAnswer(q.id, oi)}
                        style={{
                          display:'flex', alignItems:'center', gap:12,
                          padding:'12px 16px', borderRadius:12, border:'none',
                          border:'2px solid ' + (sel ? '#f59e0b' : 'rgba(0,0,0,0.08)'),
                          background: sel ? 'rgba(245,158,11,0.08)' : 'rgba(0,0,0,0.01)',
                          cursor:'pointer', textAlign:'left', transition:'all 0.15s',
                        }}>
                        <div style={{ width:26, height:26, borderRadius:7, flexShrink:0,
                          background: sel ? '#f59e0b' : 'rgba(0,0,0,0.06)',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:11, fontWeight:800,
                          color: sel ? '#fff' : '#94a3b8' }}>
                          {letters[oi]}
                        </div>
                        <span style={{ fontSize:14, color: sel ? '#92400e' : '#374151',
                          fontWeight: sel ? 600 : 400, lineHeight:1.4 }}>
                          {opt}
                        </span>
                        {sel && (
                          <span style={{ marginLeft:'auto', color:'#f59e0b', fontWeight:800, fontSize:16 }}>✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Navigation */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          marginTop:8, paddingBottom:20 }}>
          <button onClick={() => navigate('prev')} disabled={page === 0}
            style={{ padding:'12px 22px', borderRadius:12,
              border:'1.5px solid rgba(0,0,0,0.1)',
              background: page === 0 ? 'rgba(0,0,0,0.03)' : '#fff',
              color: page === 0 ? '#cbd5e1' : '#374151',
              cursor: page === 0 ? 'default' : 'pointer',
              fontSize:13, fontWeight:600, transition:'all 0.15s' }}>
            ← Previous
          </button>

          <div style={{ fontSize:12, color:'#64748b', textAlign:'center' }}>
            {pageAnswered
              ? <span style={{ color:'#10b981', fontWeight:600 }}>✓ All answered</span>
              : <span style={{ color:'#f59e0b', fontWeight:500 }}>Answer all to continue</span>}
          </div>

          {page < TOTAL_PAGES - 1 ? (
            <button onClick={() => navigate('next')} disabled={!pageAnswered}
              style={{
                padding:'12px 22px', borderRadius:12, border:'none',
                background: pageAnswered ? currentSection.color : 'rgba(0,0,0,0.07)',
                color: pageAnswered ? '#fff' : '#cbd5e1',
                cursor: pageAnswered ? 'pointer' : 'default',
                fontSize:13, fontWeight:700, transition:'all 0.2s',
                boxShadow: pageAnswered ? '0 4px 16px ' + currentSection.color + '45' : 'none',
              }}>
              Next →
            </button>
          ) : (
            <button onClick={handleSubmit}
              disabled={!pageAnswered || submitting || totalAnswered < ALL_QUESTIONS.length}
              style={{
                padding:'12px 22px', borderRadius:12, border:'none',
                background: (pageAnswered && totalAnswered === ALL_QUESTIONS.length)
                  ? 'linear-gradient(135deg,#10b981,#059669)'
                  : 'rgba(0,0,0,0.07)',
                color: (pageAnswered && totalAnswered === ALL_QUESTIONS.length) ? '#fff' : '#cbd5e1',
                cursor: (pageAnswered && totalAnswered === ALL_QUESTIONS.length) ? 'pointer' : 'default',
                fontSize:13, fontWeight:700,
                boxShadow: (pageAnswered && totalAnswered === ALL_QUESTIONS.length)
                  ? '0 4px 16px rgba(16,185,129,0.4)' : 'none',
              }}>
              {submitting ? '⏳ Generating…' : '🎯 Generate My Report'}
            </button>
          )}
        </div>

        {page === TOTAL_PAGES - 1 && totalAnswered < ALL_QUESTIONS.length && (
          <div style={{ padding:'10px 16px', borderRadius:10, marginTop:4,
            background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)',
            fontSize:12, color:'#92400e', textAlign:'center' }}>
            ⚠️ {ALL_QUESTIONS.length - totalAnswered} question(s) unanswered — please go back and complete all sections.
          </div>
        )}
      </div>
    </div>
  );
}
